import { useMemo, useCallback, useTransition } from "react";
import { useRouter } from "next/navigation";
import type { PaginationModeContextValue } from "../contexts/types";

interface UseEntityPageStableCallbacksParams {
  router: ReturnType<typeof useRouter>;
  filterContext: any;
  paginationModeState: PaginationModeContextValue;
}

/**
 * Stable callback references for UniversalEntityPage data hook.
 * Extracted to keep useUniversalEntityPageData under the 300-line limit.
 * No functional changes — these were previously inline in that hook.
 */
export function useEntityPageStableCallbacks({
  router,
  filterContext,
  paginationModeState,
}: UseEntityPageStableCallbacksParams) {
  const [, startTransition] = useTransition();

  const { setPage, setPageSize, setCursor } = paginationModeState;

  // Stable noop callbacks — avoid inline `() => {}` creating new refs each render
  const stableNoop = useCallback(() => {}, []);
  const stableNoopObj = useCallback(() => ({}), []);

  const setActiveViewId = useCallback(
    (id: string | null) => {
      const url = new URL(window.location.href);
      if (id) {
        url.searchParams.set("view", id);
      } else {
        url.searchParams.delete("view");
      }
      router.push(url.pathname + url.search);
    },
    [router]
  );

  const applySavedView = useCallback(
    (viewId: string | null) => {
      startTransition(() => {
        if (filterContext) {
          filterContext.actions.applyPreset(viewId);
        }
      });
    },
    [filterContext, startTransition]
  );

  const setPagination = useCallback(
    (update: any) => {
      if (update.page !== undefined) setPage(update.page);
      if (update.pageSize !== undefined) setPageSize(update.pageSize);
      if (update.cursor !== undefined) setCursor(update.cursor);
    },
    [setPage, setPageSize, setCursor]
  );

  // Stable fallback for filterHelpers when FilterContext is unavailable
  const stableFilterHelpersFallback = useMemo(
    () => ({
      convertToGraphQLFilter: () => ({}),
      constructSearchFilter: () => ({}),
      convertAdvancedFiltersToGraphQL: () => ({}),
    }),
    []
  );

  return {
    stableNoop,
    stableNoopObj,
    setActiveViewId,
    applySavedView,
    setPagination,
    stableFilterHelpersFallback,
  };
}
